/**
 * Exposure Limits — per-market, per-sport, and total exposure checks
 *
 * Pure functions for pre-trade risk validation.
 *
 * @ai-context
 *   Role: Pre-trade risk gate. Computes dollar exposure from position data
 *         and checks against per-market, per-sport, and total limits.
 *   Pattern: Stateless pure functions. No API calls, no side effects.
 *   Purity: All exports are pure. DEFAULT_LIMITS is a frozen constant.
 *
 * @ai-invariant priceCents is always in cents (0..100), NOT fractional dollars.
 *   Callers (useDefenseData, ExposurePanel) must convert averagePrice * 100
 *   BEFORE passing to computeExposure or building PositionExposure objects.
 *   This module never converts — it trusts the caller did it right.
 *
 * @ai-invariant checkExposure is an additive check: it sums the new order's
 *   exposure ON TOP of currentPositions. It does not replace or re-derive
 *   existing exposure — just adds the delta and checks limits.
 *
 * @ai-upstream
 *   - No external module dependencies (leaf module)
 *
 * @ai-downstream
 *   - useDefenseData → computeExposure, summarizeExposure, DEFAULT_LIMITS, ExposureSummary
 *   - ExposurePanel → DEFAULT_LIMITS, ExposureSummary
 *   - exposureLimits.test.ts (15 cases across 4 describe blocks)
 */

export interface ExposureLimitConfig {
  /** Max exposure per individual market in dollars (default 10000) */
  perMarketMaxDollars: number;
  /** Max exposure per sport in dollars (default 30000) */
  perSportMaxDollars: number;
  /** Max total exposure across all positions in dollars (default 100000) */
  totalMaxDollars: number;
}

export const DEFAULT_LIMITS: ExposureLimitConfig = {
  perMarketMaxDollars: 10000,
  perSportMaxDollars: 30000,
  totalMaxDollars: 100000,
};

export interface PositionExposure {
  ticker: string;
  sport: string;
  side: 'yes' | 'no';
  quantity: number;
  priceCents: number;
  /** Exposure = cost = quantity × (priceCents/100) for YES, quantity × ((100-priceCents)/100) for NO */
  exposureDollars: number;
  /** Optional game key for game-level grouping (e.g., "2026-02-23|LAL|BOS") */
  gameKey?: string;
}

export type ExposureCheckResult =
  | { ok: true; currentExposure: ExposureSummary }
  | {
      ok: false;
      reason: string;
      code: 'per_market' | 'per_sport' | 'total';
      currentExposure: ExposureSummary;
    };

export interface ExposureSummary {
  /** Total across all positions */
  totalDollars: number;
  /** Grouped by sport */
  bySport: Map<string, number>;
  /** Grouped by market ticker */
  byMarket: Map<string, number>;
  /** Grouped by game key — worst-case P&L netted per game */
  byGame?: Map<string, number>;
}

/** Compute exposure in dollars for a position */
export function computeExposure(quantity: number, priceCents: number, side: 'yes' | 'no'): number {
  // @ai-danger: priceCents must already be in cents (0..100). This function
  //   does NOT convert from fractional dollars. Passing 0..1 → 100x undercount.
  const costCents = side === 'yes' ? priceCents : 100 - priceCents;
  // @ai-note: YES cost = price paid. NO cost = 100 - price (complementary).
  //   Both divided by 100 to get dollars. quantity is absolute (contracts).
  return (costCents / 100) * Math.abs(quantity);
}

/** Extract sport code from a ticker (e.g., "KXNBA..." → "NBA") */
export function extractSport(ticker: string): string {
  // @ai-note: Silent fallback to 'OTHER' for non-Kalshi or unparseable tickers.
  //   Not an error — Polymarket tickers don't match KX prefix.
  const match = ticker.match(/^KX([A-Z]+)/);
  return match ? match[1] : 'OTHER';
}

/** Build an exposure summary from positions */
export function summarizeExposure(positions: PositionExposure[]): ExposureSummary {
  const bySport = new Map<string, number>();
  const byMarket = new Map<string, number>();
  let totalDollars = 0;

  for (const pos of positions) {
    if (!isFinite(pos.exposureDollars) || pos.exposureDollars < 0) continue;
    totalDollars += pos.exposureDollars;
    bySport.set(pos.sport, (bySport.get(pos.sport) ?? 0) + pos.exposureDollars);
    byMarket.set(pos.ticker, (byMarket.get(pos.ticker) ?? 0) + pos.exposureDollars);
  }

  return { totalDollars, bySport, byMarket };
}

/** Check if a new order would breach exposure limits */
export function checkExposure(
  newTicker: string,
  newSide: 'yes' | 'no',
  newQuantity: number,
  newPriceCents: number,
  currentPositions: PositionExposure[],
  limits?: Partial<ExposureLimitConfig>
): ExposureCheckResult {
  const config = { ...DEFAULT_LIMITS, ...limits };
  const newExposure = computeExposure(newQuantity, newPriceCents, newSide);
  const newSport = extractSport(newTicker);

  // Compute current exposure summary
  const current = summarizeExposure(currentPositions);

  // @ai-note: Checks are sequential: per-market → per-sport → total.
  //   First breach wins. This means a per-market breach masks a total breach.
  const marketExposure = (current.byMarket.get(newTicker) ?? 0) + newExposure;
  if (marketExposure > config.perMarketMaxDollars) {
    return {
      ok: false,
      reason: `Market exposure $${marketExposure.toFixed(0)} would exceed $${config.perMarketMaxDollars.toLocaleString()} limit`,
      code: 'per_market',
      currentExposure: current,
    };
  }

  // Check per-sport
  const sportExposure = (current.bySport.get(newSport) ?? 0) + newExposure;
  if (sportExposure > config.perSportMaxDollars) {
    return {
      ok: false,
      reason: `${newSport} exposure $${sportExposure.toFixed(0)} would exceed $${config.perSportMaxDollars.toLocaleString()} limit`,
      code: 'per_sport',
      currentExposure: current,
    };
  }

  // Check total
  const totalExposure = current.totalDollars + newExposure;
  if (totalExposure > config.totalMaxDollars) {
    return {
      ok: false,
      reason: `Total exposure $${totalExposure.toFixed(0)} would exceed $${config.totalMaxDollars.toLocaleString()} limit`,
      code: 'total',
      currentExposure: current,
    };
  }

  return { ok: true, currentExposure: current };
}

/* ------------------------------------------------------------------ */
/*  Game-level worst-case P&L netting (F1)                             */
/* ------------------------------------------------------------------ */

/**
 * Compute worst-case exposure for a set of positions on the same game.
 *
 * A game has N binary-outcome tickers (each settles $1 YES or $0 YES).
 * Exactly one ticker in the game resolves YES; all others resolve NO.
 *
 * For each possible winning ticker, compute the net P&L across all legs:
 *   - Winning ticker YES holders get $1/contract; NO holders lose their cost
 *   - Losing ticker YES holders lose their cost; NO holders get $1/contract
 *
 * Worst-case exposure = max loss (most negative P&L) across all scenarios.
 * If no scenario produces a loss, worst case is 0.
 */
export function computeGameWorstCase(positions: PositionExposure[]): number {
  if (positions.length === 0) return 0;

  // Collect all unique tickers (possible outcomes)
  const tickers = [...new Set(positions.map((p) => p.ticker))];

  // If only one ticker, worst case = cost basis (no netting possible)
  if (tickers.length <= 1) {
    return positions.reduce((sum, p) => sum + p.exposureDollars, 0);
  }

  let worstLoss = 0;

  // Evaluate each scenario: "what if ticker X wins?"
  for (const winningTicker of tickers) {
    let scenarioPnl = 0;

    for (const pos of positions) {
      const qty = Math.abs(pos.quantity);
      const cost = pos.exposureDollars;

      if (pos.ticker === winningTicker) {
        // This ticker settles YES=100¢
        if (pos.side === 'yes') {
          // Payout $1/contract, paid cost
          scenarioPnl += qty - cost;
        } else {
          // NO loses — paid cost, gets nothing
          scenarioPnl -= cost;
        }
      } else {
        // This ticker settles YES=0¢
        if (pos.side === 'yes') {
          // YES loses — paid cost, gets nothing
          scenarioPnl -= cost;
        } else {
          // NO wins — payout $1/contract, paid cost
          scenarioPnl += qty - cost;
        }
      }
    }

    // Worst case = most negative scenario
    if (scenarioPnl < worstLoss) {
      worstLoss = scenarioPnl;
    }
  }

  // Return absolute loss (positive number representing dollars at risk)
  return Math.abs(worstLoss);
}

/**
 * Build game-level exposure summary using worst-case netting.
 * Positions with gameKey are grouped and netted; those without fall back to cost sum.
 */
export function summarizeExposureWithGames(positions: PositionExposure[]): ExposureSummary {
  const base = summarizeExposure(positions);

  // Group by gameKey
  const gameGroups = new Map<string, PositionExposure[]>();
  for (const pos of positions) {
    if (!isFinite(pos.exposureDollars) || pos.exposureDollars < 0) continue;
    const key = pos.gameKey ?? pos.ticker; // ungrouped tickers use ticker as key
    const group = gameGroups.get(key);
    if (group) {
      group.push(pos);
    } else {
      gameGroups.set(key, [pos]);
    }
  }

  const byGame = new Map<string, number>();
  for (const [key, group] of gameGroups) {
    byGame.set(key, computeGameWorstCase(group));
  }

  return { ...base, byGame };
}

/* ------------------------------------------------------------------ */
/*  Concentration metrics (F2)                                         */
/* ------------------------------------------------------------------ */

export type ConcentrationSeverity = 'green' | 'yellow' | 'orange' | 'red';

export interface ConcentrationMetrics {
  /** Top game key (or ticker if no game grouping) */
  topGameKey: string;
  topGameDollars: number;
  topGamePercent: number; // 0-100
  topSportName: string;
  topSportDollars: number;
  topSportPercent: number; // 0-100
  gameSeverity: ConcentrationSeverity;
  sportSeverity: ConcentrationSeverity;
  /** @deprecated Use topGameKey — kept for backward compat */
  topMarketTicker: string;
  topMarketDollars: number;
  topMarketPercent: number; // 0-100
  marketSeverity: ConcentrationSeverity;
}

/** Map a 0-100 percentage to a severity color bucket. */
export function concentrationSeverity(percent: number): ConcentrationSeverity {
  if (percent >= 70) return 'red';
  if (percent >= 50) return 'orange';
  if (percent >= 30) return 'yellow';
  return 'green';
}

/**
 * Derive concentration metrics from an ExposureSummary.
 *
 * Returns the top market and top sport by dollar exposure, their
 * percentage of total, and a severity rating for each.
 */
export function computeConcentration(summary: ExposureSummary): ConcentrationMetrics {
  // Game-level concentration (preferred when byGame is available)
  let topGameKey = '';
  let topGameDollars = 0;
  const gameSource = summary.byGame ?? summary.byMarket;
  for (const [key, dollars] of gameSource) {
    if (dollars > topGameDollars) {
      topGameKey = key;
      topGameDollars = dollars;
    }
  }

  // Per-market (legacy, kept for backward compat)
  let topMarketTicker = '';
  let topMarketDollars = 0;
  for (const [ticker, dollars] of summary.byMarket) {
    if (dollars > topMarketDollars) {
      topMarketTicker = ticker;
      topMarketDollars = dollars;
    }
  }

  let topSportName = '';
  let topSportDollars = 0;
  for (const [sport, dollars] of summary.bySport) {
    if (dollars > topSportDollars) {
      topSportName = sport;
      topSportDollars = dollars;
    }
  }

  const total = summary.totalDollars;
  const topGamePercent = total > 0 ? (topGameDollars / total) * 100 : 0;
  const topMarketPercent = total > 0 ? (topMarketDollars / total) * 100 : 0;
  const topSportPercent = total > 0 ? (topSportDollars / total) * 100 : 0;

  return {
    topGameKey,
    topGameDollars,
    topGamePercent,
    gameSeverity: concentrationSeverity(topGamePercent),
    topMarketTicker,
    topMarketDollars,
    topMarketPercent,
    marketSeverity: concentrationSeverity(topMarketPercent),
    topSportName,
    topSportDollars,
    topSportPercent,
    sportSeverity: concentrationSeverity(topSportPercent),
  };
}

/**
 * @ai-modification-checklist
 *
 * Before modifying this file, verify:
 *
 * 1. UNIT CONTRACT — This module expects priceCents (0..100). It never
 *    converts from fractional dollars. All callers must convert first.
 *
 * 2. YES/NO COMPLEMENT — YES cost = priceCents, NO cost = 100 - priceCents.
 *    If adding a new side type, update computeExposure.
 *
 * 3. CHECK ORDER — checkExposure returns the FIRST breach found (per-market
 *    before per-sport before total). Don't reorder without understanding
 *    that downstream code may rely on the priority.
 *
 * 4. DEFAULT_LIMITS — Changing defaults affects useDefenseData posture
 *    calculation and ExposurePanel display. Coordinate across both.
 *
 * 5. TEST FILE — exposureLimits.test.ts (15 cases across 4 describe blocks).
 *    Run: npm run dashboard:test -- exposureLimits
 */
